import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { assetsForGrid, normaliseAssetGrids } from '../utils/assetGrids';
import AssetGallery from './AssetGallery';
import RichContent from './RichContent';
import ShareListTable from './ShareListTable';

const isValidHexColor = (value = '') => /^#(?:[0-9a-f]{3}){1,2}$/i.test(value.trim());

function AssetGridGallery({
  addToCartDisabled = false,
  activeGridId: controlledActiveGridId,
  assets,
  assetFields,
  cartButtonLabel,
  columns,
  enableBookingActions = false,
  focusedAssetId = '',
  layout,
  link,
  onAddToCart,
  onAddToItemForm,
  itemFormButtonText,
  onAddUpdate,
  onActiveGridChange,
  onAddToBooking,
  onEnquire,
  onListInteraction,
  onShare,
  permissions,
  searchQuery = '',
  shareDescription,
  shareTitle,
  shareUrl,
  showListingFilters = true,
  showShareAction = true,
}) {
  const grids = useMemo(() => normaliseAssetGrids({ ...link, assets }), [assets, link]);
  const [internalActiveGridId, setInternalActiveGridId] = useState(grids[0]?.id || '');
  const activeGridId = controlledActiveGridId ?? internalActiveGridId;
  const activeGrid = grids.find((grid) => grid.id === activeGridId) || grids[0];
  const activeGridCategory = activeGrid?.category || link?.category || 'album';
  const isCheckoutGrid = ['store', 'menu'].includes(activeGridCategory);
  const isBookingGrid = activeGridCategory === 'booking' || enableBookingActions;
  const isRegisterGrid = activeGridCategory === 'register';
  const activeAssets = assetsForGrid(assets, activeGrid)
    .filter((asset) => !focusedAssetId || asset.id === focusedAssetId)
    .filter((asset) => (
      !isCheckoutGrid || Number(asset.quantity) > 0
    ));
  const assetCardBackgroundColor = isValidHexColor(link?.assetCardBackgroundColor)
    ? link.assetCardBackgroundColor.trim()
    : '#ffffff';

  const updateActiveGrid = useCallback((gridId) => {
    setInternalActiveGridId(gridId);
    onActiveGridChange?.(gridId);
  }, [onActiveGridChange]);

  useEffect(() => {
    if (grids.some((grid) => grid.id === activeGridId)) return;
    updateActiveGrid(grids[0]?.id || '');
  }, [activeGridId, grids, updateActiveGrid]);

  return (
    <section className="asset-grid-gallery">
      {grids.length > 1 && !focusedAssetId && (
        <nav className="asset-grid-nav" aria-label="Asset grids">
          {grids.map((grid) => (
            <button
              aria-pressed={activeGrid?.id === grid.id}
              className={activeGrid?.id === grid.id ? 'asset-grid-nav-button active' : 'asset-grid-nav-button'}
              key={grid.id}
              onClick={() => updateActiveGrid(grid.id)}
              type="button"
            >
              <span>{grid.title}</span>
              <span className="asset-grid-count-badge">{grid.assetIds?.length || 0}</span>
            </button>
          ))}
        </nav>
      )}

      {activeGrid?.type === 'list' ? (
        <ShareListTable
          grid={activeGrid}
          link={link}
          onInteraction={onListInteraction}
          searchQuery={searchQuery}
        />
      ) : activeGrid?.type === 'rich-content' ? (
        <section className="rich-content-grid-renderer">
          {activeGrid.richContentTitle && (
            <h2>{activeGrid.richContentTitle}</h2>
          )}
          {activeGrid.richContentMarkdown ? (
            <RichContent>{activeGrid.richContentMarkdown}</RichContent>
          ) : (
            <p className="text-sm leading-6 text-slate-500">No rich content has been added to this grid.</p>
          )}
        </section>
      ) : (
      <AssetGallery
        addToCartDisabled={addToCartDisabled}
        assets={activeAssets}
        assetCardBackgroundColor={assetCardBackgroundColor}
        assetFields={assetFields}
        cartButtonLabel={cartButtonLabel}
        columns={activeGrid?.columns || columns}
        listingType={activeGridCategory === 'listing' ? activeGrid?.listingType : ''}
        link={link}
        layout={activeGrid?.layout || layout}
        enableHtmlEmbedFullscreen={activeGrid?.type === 'presentation'}
        enablePdfPresentation={activeGrid?.type === 'presentation'}
        onAddToCart={isCheckoutGrid ? onAddToCart : isBookingGrid ? onAddToBooking : undefined}
        onAddToItemForm={onAddToItemForm}
        itemFormButtonText={itemFormButtonText}
        onAddUpdate={isRegisterGrid ? onAddUpdate : undefined}
        onEnquire={activeGridCategory === 'listing' ? onEnquire : undefined}
        onShare={onShare}
        permissions={permissions}
        searchQuery={searchQuery}
        shareDescription={shareDescription}
        shareTitle={shareTitle}
        shareUrl={shareUrl}
        showListingFilters={showListingFilters}
        showShareAction={showShareAction}
        showStoreQuantityHint={isCheckoutGrid}
      />
      )}
    </section>
  );
}

export default AssetGridGallery;
